import React, { useEffect, useState } from "react";
import { pausableInterval } from "@/lib/appVisibility";
import { useApp } from "@/context/AppContext";
import { isOffline } from "@/lib/netStatus";
import { Loader2, WifiOff } from "lucide-react";

// Shown INSTEAD of the login page when a session exists but the server is unreachable (no network, deploy
// window, infra blip) AND no session is cached yet (1er lancement hors ligne). The token is kept and the app
// reconnects automatically (network back, app to foreground, every 5 s) — the member never has to log in again.
export const ReconnectOverlay = () => {
  const { reconnecting, reconnectNow, user, t } = useApp();
  const [offline, setOffline] = useState(isOffline());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!reconnecting) return undefined;
    const sync = () => setOffline(isOffline());
    sync();
    const stop = pausableInterval(sync, 2000); // lot 699 — repos en arrière-plan
    window.addEventListener("online", sync);
    window.addEventListener("offline", sync);
    window.addEventListener("yalola:network-online", sync);
    return () => { stop(); window.removeEventListener("online", sync); window.removeEventListener("offline", sync); window.removeEventListener("yalola:network-online", sync); };
  }, [reconnecting]);
  // Session connue sur l'appareil → mode hors ligne (bandeau AppShell/OfflineBanner), pas d'écran plein.
  if (!reconnecting || user) return null;
  const retry = async () => {
    setBusy(true);
    try { await reconnectNow(); } finally { setTimeout(() => setBusy(false), 1500); }
  };
  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4 px-8 text-center"
      style={{ background: "var(--yalola-bg, #FAF7F2)" }}
      data-testid="reconnect-overlay"
      data-offline={offline ? "1" : "0"}
    >
      <img src="/logo192.png" alt="Yalola" className="w-16 h-16 rounded-2xl" />
      {offline ? <WifiOff size={26} style={{ color: "var(--yalola-primary, #D97757)" }} /> : <Loader2 className="animate-spin" size={26} style={{ color: "var(--yalola-primary, #D97757)" }} />}
      <div className="font-bold font-display text-lg" data-testid="reconnect-title">
        {offline ? t("reconnect.offline_title", "Pas de connexion internet") : t("reconnect.title", "Reconnexion en cours…")}
      </div>
      <p className="text-sm max-w-sm" style={{ color: "var(--yalola-text-muted, #6B7280)" }} data-testid="reconnect-desc">
        {offline
          ? t("reconnect.offline_desc", "Vérifie tes données mobiles ou le Wi-Fi. Ta session est conservée : l'app reprendra toute seule dès que la connexion revient.")
          : t("reconnect.desc", "Ta session est conservée. L'app se reconnecte automatiquement dès que le serveur répond.")}
      </p>
      <button
        type="button"
        className="mt-2 rounded-full px-5 py-2 text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-70"
        style={{ background: "var(--yalola-primary, #D97757)" }}
        onClick={retry}
        disabled={busy}
        data-testid="reconnect-retry-btn"
      >
        {busy && <Loader2 className="animate-spin" size={15} />}
        {t("reconnect.retry", "Réessayer maintenant")}
      </button>
    </div>
  );
};
